import { z } from "zod";
import {
	monedaSchema,
	requiredPositiveNumber,
	requiredString,
	requiredDateString,
	optionalString,
	timestampFieldsSchema,
	createPaginatedSchema,
	listParamsSchema,
	tipoGastoSchema,
	tipoIngresoSchema,
	fuenteValorizacionSchema,
	periodicidadSchema,
	TIPOS_GASTO,
	TIPOS_INGRESO,
	FUENTES_VALORIZACION,
	PERIODICIDADES,
} from "@schemas/common";

// =============================================================================
// FINANCIAL SUMMARY
// =============================================================================

export const costosFinancierosSchema = z.object({
	interes: z.number(),
	seguro_desgravamen: z.number(),
	seguro_bien: z.number(),
	portes: z.number(),
	capital: z.number(),
	total_sin_capital: z.number(),
	total: z.number(),
	moneda_origen: z.string(),
	tasa_conversion: z.number().nullable().optional(),
	tasa_fecha: z.string().nullable().optional(),
});

export const resumenPeriodoSchema = z.object({
	periodo: z.string(),
	total_ingresos: z.number(),
	gastos_operativos: z.number(),
	noi: z.number(),
	costos_financieros: costosFinancierosSchema.nullable().optional(),
	flujo_caja_neta: z.number(),
	total_amortizacion_periodo: z.number().nullable().optional(),
	total_principal_pagado_periodo: z.number().nullable().optional(),
	tasa_promedio: z.number().nullable().optional(),
	notas: z.array(z.string()).default([]),
});

export const financialsSummaryResponseSchema = z.object({
	moneda_destino: z.string(),
	agrupacion: z.string(),
	hipoteca_activa_id: z.number().nullable().optional(),
	inversion_inicial: z.number().nullable().optional(),
	resumen: resumenPeriodoSchema,
	periodos: z.array(resumenPeriodoSchema),
});

export const financialsSummaryParamsSchema = z.object({
	range: z.string().optional(),
	moneda_destino: monedaSchema.optional(),
	fecha_inicio: z.string().optional(),
	fecha_fin: z.string().optional(),
});

// =============================================================================
// NEW SCREEN-LEVEL FINANCIAL SUMMARY
// =============================================================================

export const ingresosBreakdownSchema = z.object({
	por_tipo: z.record(z.string(), z.number()),
});

export const interesesYSegurosBreakdownSchema = z.object({
	interes: z.number(),
	seguro_desgravamen: z.number(),
	seguro_bien: z.number(),
	portes: z.number(),
});

export const pagosCapitalBreakdownSchema = z.object({
	capital_cuota: z.number(),
	amortizaciones_extra: z.number(),
});

export const flujoRealBreakdownSchema = z.object({
	ingresos: z.number(),
	gastos: z.number(),
	intereses_y_seguros: z.number(),
});

export const flujoNetoBreakdownSchema = z.object({
	ingresos: z.number(),
	pagos_a_capital: z.number(),
	gastos: z.number(),
	intereses_y_seguros: z.number(),
});

export const financialsIngresoMetricSchema = z.object({
	total: z.number(),
	trend_pct: z.number().nullable().optional(),
	breakdown: ingresosBreakdownSchema,
});

export const financialsGastoMetricSchema = z.object({
	total: z.number(),
	trend_pct: z.number().nullable().optional(),
	breakdown: z.record(z.string(), z.number()),
});

export const financialsInteresesMetricSchema = z.object({
	total: z.number(),
	trend_pct: z.number().nullable().optional(),
	breakdown: interesesYSegurosBreakdownSchema,
});

export const financialsCapitalMetricSchema = z.object({
	total: z.number(),
	trend_pct: z.number().nullable().optional(),
	breakdown: pagosCapitalBreakdownSchema,
});

export const financialsFlujoRealMetricSchema = z.object({
	total: z.number(),
	trend_pct: z.number().nullable().optional(),
	breakdown: flujoRealBreakdownSchema,
});

export const financialsFlujoNetoMetricSchema = z.object({
	total: z.number(),
	trend_pct: z.number().nullable().optional(),
	breakdown: flujoNetoBreakdownSchema,
});

export const financialsSummaryKPIsSchema = z.object({
	total_ingresos: financialsIngresoMetricSchema,
	total_gastos: financialsGastoMetricSchema,
	intereses_y_seguros: financialsInteresesMetricSchema,
	pagos_a_capital: financialsCapitalMetricSchema,
	flujo_real: financialsFlujoRealMetricSchema,
	flujo_neto: financialsFlujoNetoMetricSchema,
});

export const financialsChartPointSchema = z.object({
	periodo: z.string(),
	ingresos: z.number(),
	gastos: z.number(),
	pagos_a_capital: z.number(),
});

export const financialsScreenMetaSchema = z.object({
	moneda: monedaSchema,
});

export const financialsScreenResponseSchema = z.object({
	meta: financialsScreenMetaSchema,
	summary: financialsSummaryKPIsSchema,
	chart: z.array(financialsChartPointSchema),
});

// =============================================================================
// RENTAL INVESTMENT ANALYSIS (/propiedad/{id}/finanzas/analisis)
// =============================================================================

export const analisisItemDetalleGastoSchema = z.object({
	descripcion: z.string().nullable().optional(),
	tipo: z.string(),
	monto: z.number(),
	moneda_original: z.string(),
	fecha: z.string(),
});

export const analisisInversionInicialSchema = z.object({
	cuota_inicial: z.number(),
	costos_de_compra: z.number(),
	detalle_costos: z.array(analisisItemDetalleGastoSchema).default([]),
	total: z.number(),
});

export const analisisSimulacionDetalleSchema = z.object({
	tiene_simulacion: z.boolean().default(false),
	ingresos_reales: z.number().default(0),
	ingresos_simulados: z.number().default(0),
	tasa_crecimiento_anual: z.number().nullable().optional(),
	tasa_vacancia_anual: z.number().nullable().optional(),
});

export const analisisResumenIngresosSchema = z.object({
	alquiler: z.number(),
	alquiler_temporal: z.number(),
	otro: z.number(),
	total: z.number(),
	total_real: z.number(),
	total_proyectado: z.number(),
	ingreso_anual_actual: z.number(),
	simulacion: analisisSimulacionDetalleSchema,
});

export const analisisResumenGastosOperativosSchema = z.object({
	mantenimiento: z.number(),
	predial: z.number(),
	arbitrios: z.number(),
	seguro_vida: z.number(),
	reparacion: z.number(),
	remodelacion: z.number(),
	implementacion: z.number(),
	agente_inmobiliario: z.number(),
	otros: z.number(),
	total: z.number(),
	total_real: z.number(),
	total_proyectado: z.number(),
});

export const analisisResumenCostosHipotecariosSchema = z.object({
	intereses: z.number(),
	seguro_desgravamen: z.number(),
	seguro_bien: z.number(),
	portes: z.number(),
	total: z.number(),
	total_real: z.number(),
	total_proyectado: z.number(),
});

export const analisisResumenGastosSchema = z.object({
	operativos: analisisResumenGastosOperativosSchema,
	hipotecarios: analisisResumenCostosHipotecariosSchema,
	total: z.number(),
});

export const analisisResumenFlujoSchema = z.object({
	ingresos_brutos: z.number(),
	gastos_operativos: z.number(),
	noi_acumulado: z.number(),
	costos_hipotecarios: z.number(),
	flujo_neto_acumulado: z.number(),
	noi_anual: z.number(),
	flujo_anual: z.number(),
	flujo_mensual: z.number(),
	flujo_acumulado_a_hoy: z.number(),
});

export const analisisSerieIngresoSchema = z.object({
	total: z.number(),
	alquiler: z.number().optional(),
	alquiler_temporal: z.number().optional(),
	otro: z.number().optional(),
	alquiler_real: z.number().optional(),
	alquiler_simulado: z.number().optional(),
});

export const analisisSerieGastoSchema = z.object({
	total: z.number(),
	mantenimiento: z.number().optional(),
	predial: z.number().optional(),
	arbitrios: z.number().optional(),
	seguro_vida: z.number().optional(),
	reparacion: z.number().optional(),
	remodelacion: z.number().optional(),
	implementacion: z.number().optional(),
	agente_inmobiliario: z.number().optional(),
	otros: z.number().optional(),
});

export const analisisSerieMortgageSchema = z.object({
	intereses: z.number(),
	seguros: z.number(),
	comisiones: z.number(),
	total: z.number(),
});

export const analisisSerieFlujoSchema = z.object({
	monto_anual: z.number(),
	total_acumulado: z.number(),
});

export const analisisSeriePointSchema = z.object({
	anio: z.number(),
	meses: z.number(),
	ingresos: analisisSerieIngresoSchema,
	gastos: analisisSerieGastoSchema,
	mortgage: analisisSerieMortgageSchema,
	flujo: analisisSerieFlujoSchema,
	/**
	 * Per-year indicators. BreakdownTable's Cash-on-Cash column reads this, but
	 * the field was never declared — and zod strips what it does not know, so
	 * the column has always shown nothing. Optional because it is unclear
	 * whether every response carries it.
	 */
	indicadores: z
		.object({ cash_on_cash: z.number().nullable() })
		.optional(),
});

/** A projection milestone (payback / doubling the initial investment). */
export const analisisHitoSchema = z.object({
	alcanzado: z.boolean(),
	proyectado: z.boolean(),
	anio: z.number().nullable(),
	anios_desde_inicio: z.number().nullable(),
});

export const analisisRoiSchema = z.object({
	porcentaje: z.number(),
	monto: z.number(),
	anualizado: z.number(),
});

export const analisisTirSchema = z.object({
	porcentaje: z.number(),
});

export const analisisAlquilerResponseSchema = z.object({
	moneda_destino: z.string(),
	fecha_inicio: z.string(),
	fecha_fin: z.string(),
	inversion_inicial: analisisInversionInicialSchema,
	ingresos: analisisResumenIngresosSchema,
	gastos: analisisResumenGastosSchema,
	flujo: analisisResumenFlujoSchema,
	serie: z.array(analisisSeriePointSchema),
	payback: analisisHitoSchema,
	inversion_doble: analisisHitoSchema,
	cash_on_cash_total: z.number().nullable().optional(),
	cash_on_cash_anual: z.number().nullable().optional(),
	tir: analisisTirSchema.nullable().optional(),
	yield_bruto: z.number().nullable().optional(),
	yield_neto: z.number().nullable().optional(),
	cap_rate: z.number().nullable().optional(),
	precio_compra: z.number(),
	valor_propiedad: z.number(),
});

export const analisisParamsSchema = z.object({
	fecha_inicio: z.string().optional(),
	fecha_fin: z.string().optional(),
	moneda_destino: monedaSchema.optional(),
});

export type AnalisisAlquilerResponse = z.infer<typeof analisisAlquilerResponseSchema>;
export type AnalisisParams = z.infer<typeof analisisParamsSchema>;
export type AnalisisSeriePoint = z.infer<typeof analisisSeriePointSchema>;
export type AnalisisSerieIngreso = z.infer<typeof analisisSerieIngresoSchema>;
export type AnalisisSerieGasto = z.infer<typeof analisisSerieGastoSchema>;
export type AnalisisSerieMortgage = z.infer<typeof analisisSerieMortgageSchema>;
export type AnalisisSerieFlujo = z.infer<typeof analisisSerieFlujoSchema>;
export type AnalisisHito = z.infer<typeof analisisHitoSchema>;
export type AnalisisInversionInicial = z.infer<typeof analisisInversionInicialSchema>;
export type AnalisisResumenIngresos = z.infer<typeof analisisResumenIngresosSchema>;
export type AnalisisResumenGastos = z.infer<typeof analisisResumenGastosSchema>;
export type AnalisisResumenFlujo = z.infer<typeof analisisResumenFlujoSchema>;

export type CostosFinancieros = z.infer<typeof costosFinancierosSchema>;
export type ResumenPeriodo = z.infer<typeof resumenPeriodoSchema>;
export type FinancialsSummaryResponse = z.infer<
	typeof financialsSummaryResponseSchema
>;
export type FinancialsScreenResponse = z.infer<
	typeof financialsScreenResponseSchema
>;
export type FinancialsSummaryKPIs = z.infer<typeof financialsSummaryKPIsSchema>;
export type FinancialsIngresoMetric = z.infer<typeof financialsIngresoMetricSchema>;
export type FinancialsGastoMetric = z.infer<typeof financialsGastoMetricSchema>;
export type FinancialsInteresesMetric = z.infer<typeof financialsInteresesMetricSchema>;
export type FinancialsCapitalMetric = z.infer<typeof financialsCapitalMetricSchema>;
export type FinancialsFlujoRealMetric = z.infer<typeof financialsFlujoRealMetricSchema>;
export type FinancialsFlujoNetoMetric = z.infer<typeof financialsFlujoNetoMetricSchema>;
export type FinancialsChartPoint = z.infer<typeof financialsChartPointSchema>;

export type FinancialsSummaryParams = z.infer<
	typeof financialsSummaryParamsSchema
>;

// =============================================================================
// RE-EXPORTED ENUMS & TYPES (from @schemas/common for convenience)
// =============================================================================

export {
	TIPOS_GASTO,
	TIPOS_INGRESO,
	FUENTES_VALORIZACION,
	PERIODICIDADES,
	tipoGastoSchema,
	tipoIngresoSchema,
	fuenteValorizacionSchema,
	periodicidadSchema,
};

export type TipoGasto = z.infer<typeof tipoGastoSchema>;
export type TipoIngreso = z.infer<typeof tipoIngresoSchema>;
export type FuenteValorizacion = z.infer<typeof fuenteValorizacionSchema>;
export type Periodicidad = z.infer<typeof periodicidadSchema>;

// =============================================================================
// GASTOS (EXPENSES)
// =============================================================================

export const gastoBaseSchema = z.object({
	tipo: tipoGastoSchema,
	monto: requiredPositiveNumber,
	moneda: monedaSchema,
	periodicidad: periodicidadSchema,
	fecha_inicio: requiredDateString,
	fecha_fin: optionalString,
	recurrente: z.boolean().default(true),
	activo: z.boolean().default(true),
	simulacion: z.boolean().default(false),
	descripcion: optionalString,
	metadatos: z.record(z.unknown()).optional(),
});

export const gastoCreateSchema = gastoBaseSchema.superRefine((data, ctx) => {
	if (data.periodicidad !== "unica" && !data.fecha_fin) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: "required_date",
			path: ["fecha_fin"],
		});
	}
});

export const gastoUpdateSchema = gastoBaseSchema.partial();
export const gastoResponseSchema = gastoBaseSchema
	.merge(timestampFieldsSchema)
	.extend({
		id: z.number(),
		id_propiedad: z.number(),
	});

export const gastoPaginatedSchema = createPaginatedSchema(gastoResponseSchema);
export const gastoListParamsSchema = listParamsSchema.extend({
	activo: z.boolean().optional(),
});

export type GastoCreate = z.infer<typeof gastoCreateSchema>;
/** Form-side shape: the `.default()` fields are still optional here. */
export type GastoCreateInput = z.input<typeof gastoCreateSchema>;
export type GastoUpdate = z.infer<typeof gastoUpdateSchema>;
export type PropGasto = z.infer<typeof gastoResponseSchema>;
export type GastoPaginated = z.infer<typeof gastoPaginatedSchema>;
export type GastoListParams = z.infer<typeof gastoListParamsSchema>;

// =============================================================================
// INGRESOS (INCOMES)
// =============================================================================

export const ingresoBaseSchema = z.object({
	tipo: tipoIngresoSchema,
	monto: requiredPositiveNumber,
	moneda: monedaSchema,
	periodicidad: periodicidadSchema,
	fecha_inicio: requiredDateString,
	fecha_fin: optionalString,
	recurrente: z.boolean().default(true),
	activo: z.boolean().default(true),
	simulacion: z.boolean().default(false),
	descripcion: optionalString,
	metadatos: z.record(z.unknown()).optional(),
});

export const ingresoCreateSchema = ingresoBaseSchema.superRefine((data, ctx) => {
	if (data.periodicidad !== "unica" && !data.fecha_fin) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: "required_date",
			path: ["fecha_fin"],
		});
	}
});
export const ingresoUpdateSchema = ingresoBaseSchema.partial();
export const ingresoResponseSchema = ingresoBaseSchema
	.merge(timestampFieldsSchema)
	.extend({
		id: z.number(),
		id_propiedad: z.number(),
	});

export const ingresoPaginatedSchema = createPaginatedSchema(
	ingresoResponseSchema,
);
export const ingresoListParamsSchema = listParamsSchema.extend({
	activo: z.boolean().optional(),
});

export type IngresoCreate = z.infer<typeof ingresoCreateSchema>;
/** Form-side shape: the `.default()` fields are still optional here. */
export type IngresoCreateInput = z.input<typeof ingresoCreateSchema>;
export type IngresoUpdate = z.infer<typeof ingresoUpdateSchema>;
export type PropIngreso = z.infer<typeof ingresoResponseSchema>;
export type IngresoPaginated = z.infer<typeof ingresoPaginatedSchema>;
export type IngresoListParams = z.infer<typeof ingresoListParamsSchema>;

// -----------------------------------------------------------------------------
// RENTAL INCOME FORM (Nuevo Alquiler modal)
// -----------------------------------------------------------------------------

/**
 * Form schema for the "Nuevo Alquiler" modal. Validated with zod via
 * react-hook-form. The extra assumptions (mode, vacancy, growth, tax) are
 * mapped into an IngresoCreate on submit — see rentalFormToIngresoCreate.
 */
export const rentalIncomeFormSchema = z
	.object({
		mode: z.enum(["simular", "registrar"]),
		tipo: z.enum(["alquiler", "alquiler_temporal"]),
		moneda: monedaSchema,
		monto: requiredPositiveNumber,
		reducirImpuesto: z.boolean().default(false),
		fecha_inicio: requiredDateString,
		fecha_fin: optionalString,
		vacancia_pct: z.number().min(0).max(100).default(5),
		crecimiento_pct: z.number().min(0).default(3),
	})
	.superRefine((data, ctx) => {
		if (data.mode === "registrar" && !data.fecha_fin) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "required_date",
				path: ["fecha_fin"],
			});
		}
		if (
			data.mode === "registrar" &&
			data.fecha_fin &&
			data.fecha_fin < data.fecha_inicio
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "end_date_after_start",
				path: ["fecha_fin"],
			});
		}
	});

export type RentalIncomeFormData = z.infer<typeof rentalIncomeFormSchema>;
/**
 * What the form holds before the schema runs. Fields with `.default()` are
 * optional here and required in `RentalIncomeFormData`, which is why useForm
 * needs both: `useForm<Input, unknown, Output>`.
 */
export type RentalIncomeFormInput = z.input<typeof rentalIncomeFormSchema>;

const DAYS_IN_YEAR = 365;

/** days of vacancy ⇄ % of the year (source of truth is the %). */
export const vacancyPctToDays = (pct: number): number =>
	Math.round(((DAYS_IN_YEAR * pct) / 100) * 100) / 100;
export const vacancyDaysToPct = (days: number): number =>
	Math.round((days / DAYS_IN_YEAR) * 100 * 100) / 100;

export function rentalFormToIngresoCreate(
	data: RentalIncomeFormData,
): IngresoCreate {
	return {
		tipo: data.tipo,
		monto: data.monto,
		moneda: data.moneda,
		periodicidad: "mensual",
		fecha_inicio: data.fecha_inicio,
		// A simulation is open-ended by definition — that is the whole promise
		// of the "Sin fecha de fin" mode. Editing a registered rental into a
		// simulation leaves the old end date sitting in the form, so drop it
		// here rather than saving a projection that quietly stops.
		fecha_fin: data.mode === "simular" ? undefined : data.fecha_fin,
		recurrente: true,
		activo: true,
		simulacion: data.mode === "simular",
		metadatos: {
			es_simulacion: data.mode === "simular",
			reducir_impuesto_renta: data.reducirImpuesto,
			vacancia_pct: data.vacancia_pct,
			vacancia_anual: data.vacancia_pct,
			vacancia_dias: vacancyPctToDays(data.vacancia_pct),
			crecimiento_pct: data.crecimiento_pct,
			crecimiento_anual: data.crecimiento_pct,
		},
	};
}

// =============================================================================
// VALORIZACIONES (VALUATIONS)
// =============================================================================

export const valorizacionBaseSchema = z.object({
	fuente: fuenteValorizacionSchema.optional(),
	valor: requiredPositiveNumber,
	moneda: monedaSchema,
	fecha: requiredString,
	descripcion: optionalString,
	metadatos: z.record(z.unknown()).optional(),
});

export const valorizacionCreateSchema = valorizacionBaseSchema;
export const valorizacionUpdateSchema = valorizacionBaseSchema.partial();
export const valorizacionResponseSchema = valorizacionBaseSchema
	.merge(timestampFieldsSchema)
	.extend({
		id: z.number(),
		id_propiedad: z.number(),
	});

export const valorizacionPaginatedSchema = createPaginatedSchema(
	valorizacionResponseSchema,
);
export const valorizacionListParamsSchema = listParamsSchema.extend({
	fecha_desde: z.string().optional(),
	fecha_hasta: z.string().optional(),
});

export type ValorizacionCreate = z.infer<typeof valorizacionCreateSchema>;
export type ValorizacionUpdate = z.infer<typeof valorizacionUpdateSchema>;
export type PropValorizacion = z.infer<typeof valorizacionResponseSchema>;
export type ValorizacionPaginated = z.infer<typeof valorizacionPaginatedSchema>;
// =============================================================================
// VALUATION PROJECTION
// =============================================================================

export const valorActualSchema = z.object({
	monto: z.union([z.number(), z.string()]),
	moneda: monedaSchema,
	fecha: z.string(),
});

export const proyeccionPeriodoSchema = z.object({
	anio: z.number(),
	valorEstimado: z.number(),
	ganancia: z.number(),
	porcentajeGanancia: z.number(),
});

export const escenarioProyeccionSchema = z.object({
	tasaAnual: z.number(),
	proyeccionAnual: z.array(proyeccionPeriodoSchema),
	valorFinal: z.number(),
	gananciaTotal: z.number(),
	porcentajeTotal: z.number(),
});

export const proyeccionValorizacionSchema = z.object({
	anios: z.number(),
	agrupacion: z.string(),
	escenarios: z.record(z.string(), escenarioProyeccionSchema),
});

export const valorizacionPuntoHistorialSchema = z.object({
	fecha: z.string(),
	valor: z.union([z.number(), z.string()]),
	moneda: monedaSchema,
	fuente: fuenteValorizacionSchema.optional(),
});

export const valorizacionProyeccionResponseSchema = z.object({
	valorActual: valorActualSchema,
	historial: z.array(valorizacionPuntoHistorialSchema).optional(),
	proyeccion: proyeccionValorizacionSchema,
});

export const valorizacionProyeccionParamsSchema = z.object({
	anios_proyeccion: z.number().optional(),
	tasa_optimista: z.number().optional(),
	tasa_moderada: z.number().optional(),
	tasa_pesimista: z.number().optional(),
});

export type ValorizacionListParams = z.infer<
	typeof valorizacionListParamsSchema
>;
export type ValorizacionProyeccionResponse = z.infer<
	typeof valorizacionProyeccionResponseSchema
>;
export type ValorizacionProyeccionParams = z.infer<
	typeof valorizacionProyeccionParamsSchema
>;
export type EscenarioProyeccion = z.infer<typeof escenarioProyeccionSchema>;
export type ProyeccionPeriodo = z.infer<typeof proyeccionPeriodoSchema>;
export type ValorizacionPuntoHistorial = z.infer<
	typeof valorizacionPuntoHistorialSchema
>;
